/*!
 * AudoChat - AI Instructions Page
 */

import { useState, useEffect } from "react";

// @mui material components
import { TextField, IconButton, CircularProgress, Grid } from "@mui/material";

// Vision UI Dashboard React components
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Vision UI Dashboard React base styles
import colors from "assets/theme/base/colors";
import linearGradient from "assets/theme/functions/linearGradient";

// React icons
import { MdSave } from "react-icons/md";
import { BsRobot } from "react-icons/bs";

// Utils
import api from "utils/api";

const { secondary, info, text, dark, gradients, borderCol, success } = colors;
const { card } = gradients;

// Helper function to detect if text contains Arabic characters
const containsArabic = (text) => {
  if (!text) return false;
  const arabicRegex = /[\u0600-\u06FF]/;
  return arabicRegex.test(text);
};

function AI() {
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [aiId, setAiId] = useState(1);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Load AI prompt on mount
  useEffect(() => {
    loadAIPrompt();
  }, []);

  // Add global CSS to force dark mode for textarea
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      textarea.MuiInputBase-inputMultiline,
      textarea[placeholder*="AI prompt"],
      .MuiInputBase-inputMultiline {
        background-color: ${dark.body} !important;
        color: white !important;
        -webkit-text-fill-color: white !important;
        padding: 12px 16px !important;
        margin: 0 !important;
        width: 100% !important;
        box-sizing: border-box !important;
        font-family: "Tajawal", "Cairo", "Arial", "Helvetica", sans-serif !important;
      }
      .MuiOutlinedInput-root {
        background-color: ${dark.body} !important;
        padding: 0 !important;
        margin: 0 !important;
      }
      .MuiOutlinedInput-root .MuiInputBase-input {
        background-color: ${dark.body} !important;
        color: white !important;
        -webkit-text-fill-color: white !important;
        padding: 12px 16px !important;
        margin: 0 !important;
        width: 100% !important;
        box-sizing: border-box !important;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, [dark.body]);

  // Auto-resize textarea and force dark mode
  useEffect(() => {
    const textarea = document.querySelector('textarea[placeholder*="AI prompt"]');
    if (textarea) {
      // Force dark mode styles
      textarea.style.backgroundColor = dark.body;
      textarea.style.color = 'white';
      textarea.style.webkitTextFillColor = 'white';
      textarea.style.border = 'none';
      textarea.style.padding = '12px 16px';
      textarea.style.margin = '0';
      textarea.style.width = '100%';
      textarea.style.boxSizing = 'border-box';
      
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = 'auto';
      // Set height based on content
      textarea.style.height = `${Math.max(textarea.scrollHeight, 400)}px`;
    }
    
    // Also target by class name
    const textareaByClass = document.querySelector('.MuiInputBase-inputMultiline');
    if (textareaByClass) {
      textareaByClass.style.backgroundColor = dark.body;
      textareaByClass.style.color = 'white';
      textareaByClass.style.webkitTextFillColor = 'white';
      textareaByClass.style.padding = '12px 16px';
      textareaByClass.style.margin = '0';
      textareaByClass.style.width = '100%';
      textareaByClass.style.boxSizing = 'border-box';
    }
    
    // Remove padding from parent
    const inputRoot = document.querySelector('.MuiOutlinedInput-root');
    if (inputRoot) {
      inputRoot.style.padding = '0';
      inputRoot.style.margin = '0';
    }
  }, [prompt, dark.body]);

  const loadAIPrompt = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getAIPrompt(aiId);
      if (data) {
        setPrompt(data.prompt || "");
      } else {
        // If no data found, create default
        setPrompt("You are a helpful customer service assistant for AudoChat. Your role is to assist customers with their inquiries in a friendly and professional manner. Always be polite, clear, and helpful.");
      }
    } catch (err) {
      console.error("Error loading AI prompt:", err);
      setError("Failed to load AI prompt. Please try again.");
      // Set default values
      setPrompt("You are a helpful customer service assistant for AudoChat. Your role is to assist customers with their inquiries in a friendly and professional manner. Always be polite, clear, and helpful.");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);
      
      await api.updateAIPrompt(aiId, {
        prompt: prompt.trim(),
      });
      
      setSuccessMessage("AI instructions saved successfully!");
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error("Error saving AI prompt:", err);
      setError("Failed to save AI instructions. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <VuiBox
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "80vh",
          }}
        >
          <CircularProgress />
        </VuiBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        <VuiBox mb={3}>
          <VuiBox display="flex" alignItems="center" gap={2} mb={2}>
            <BsRobot size={24} color={info.main} />
            <VuiTypography variant="h4" fontWeight="bold" color="white">
              AI Instructions
            </VuiTypography>
          </VuiBox>
        </VuiBox>

        {error && (
          <VuiBox
            mb={3}
            p={2}
            sx={{
              backgroundColor: error.main,
              borderRadius: "8px",
              border: `1px solid ${error.main}`,
            }}
          >
            <VuiTypography variant="body2" color="white">
              {error}
            </VuiTypography>
          </VuiBox>
        )}

        {successMessage && (
          <VuiBox
            mb={3}
            p={2}
            sx={{
              backgroundColor: success.main,
              borderRadius: "8px",
              border: `1px solid ${success.main}`,
            }}
          >
            <VuiTypography variant="body2" color="white">
              {successMessage}
            </VuiTypography>
          </VuiBox>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <VuiBox
              sx={{
                background: linearGradient(card.main, card.state, card.deg),
                borderRadius: "20px",
                p: 3,
                border: `1px solid ${borderCol.navbar}`,
              }}
            >
              <VuiTypography variant="h6" fontWeight="bold" color="white" mb={2}>
                AI Prompt (ID: {aiId})
              </VuiTypography>
              <VuiBox
                sx={{
                  "& .MuiTextField-root": {
                    width: "100%",
                  },
                  "& *": {
                    "&::-webkit-input-placeholder": {
                      color: `${text.main} !important`,
                      opacity: 0.7,
                    },
                    "&::-moz-placeholder": {
                      color: `${text.main} !important`,
                      opacity: 0.7,
                    },
                    "&:-ms-input-placeholder": {
                      color: `${text.main} !important`,
                      opacity: 0.7,
                    },
                  },
                }}
              >
              <TextField
                fullWidth
                multiline
                value={prompt}
                onChange={(e) => {
                  setPrompt(e.target.value);
                  // Auto-resize
                  const textarea = e.target;
                  textarea.style.height = 'auto';
                  textarea.style.height = `${Math.max(textarea.scrollHeight, 400)}px`;
                }}
                placeholder="Enter AI prompt here..."
                dir={containsArabic(prompt) ? 'rtl' : 'ltr'}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: `${dark.body} !important`,
                    color: "white !important",
                    direction: containsArabic(prompt) ? 'rtl' : 'ltr',
                    alignItems: "flex-start",
                    padding: "0 !important",
                    margin: 0,
                    "& fieldset": {
                      borderColor: borderCol.navbar,
                      borderWidth: "2px",
                    },
                    "&:hover fieldset": {
                      borderColor: info.main,
                      borderWidth: "2px",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: info.main,
                      borderWidth: "2px",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: `${borderCol.navbar} !important`,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: `${info.main} !important`,
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: `${info.main} !important`,
                    },
                    "& textarea": {
                      overflow: "hidden !important",
                      resize: "none",
                      direction: containsArabic(prompt) ? 'rtl' : 'ltr',
                      textAlign: containsArabic(prompt) ? 'right' : 'left',
                      fontFamily: containsArabic(prompt) 
                        ? '"Tajawal", "Cairo", "Arial", "Helvetica", sans-serif' 
                        : '"Roboto", "Arial", sans-serif',
                      fontSize: "18px",
                      lineHeight: 1.5,
                      padding: "12px 16px !important",
                      margin: 0,
                      minHeight: "400px",
                      height: "auto",
                      width: "100% !important",
                      backgroundColor: `${dark.body} !important`,
                      color: "white !important",
                      WebkitTextFillColor: "white !important",
                      boxSizing: "border-box",
                    },
                  },
                  "& .MuiInputBase-input": {
                    color: "white !important",
                    fontSize: "18px",
                    fontFamily: containsArabic(prompt) 
                      ? '"Tajawal", "Cairo", "Arial", "Helvetica", sans-serif' 
                      : '"Roboto", "Arial", sans-serif',
                    backgroundColor: `${dark.body} !important`,
                    WebkitTextFillColor: "white !important",
                    padding: "12px 16px !important",
                    margin: 0,
                    width: "100% !important",
                    boxSizing: "border-box",
                  },
                  "& .MuiInputBase-inputMultiline": {
                    overflow: "hidden !important",
                    maxHeight: "none",
                    height: "auto",
                    backgroundColor: `${dark.body} !important`,
                    color: "white !important",
                    WebkitTextFillColor: "white !important",
                    padding: "12px 16px !important",
                    margin: 0,
                    width: "100% !important",
                    boxSizing: "border-box",
                  },
                  "& .MuiInputBase-root": {
                    backgroundColor: `${dark.body} !important`,
                    padding: "0 !important",
                    margin: 0,
                  },
                }}
                inputProps={{
                  style: {
                    overflow: "hidden",
                    resize: "none",
                    minHeight: "400px",
                    backgroundColor: dark.body,
                    color: "white",
                    WebkitTextFillColor: "white",
                    padding: "12px 16px",
                    margin: 0,
                    width: "100%",
                    boxSizing: "border-box",
                  },
                }}
              />
              </VuiBox>
            </VuiBox>
          </Grid>

          <Grid item xs={12}>
            <VuiBox display="flex" justifyContent="flex-end" gap={2}>
              <VuiButton
                variant="contained"
                color="info"
                onClick={handleSave}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={16} /> : <MdSave />}
              >
                {saving ? "Saving..." : "Save Changes"}
              </VuiButton>
            </VuiBox>
          </Grid>
        </Grid>
      </VuiBox>
      <Footer />
    </DashboardLayout>
  );
}

export default AI;

